import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { SearchField } from "@mapmycustomers/ui";

import { IWizardStep, StandardStep } from "@app/component/wizard";
import CardType from "@app/enum/dashboard/CardType";
import PreviewType from "@app/enum/dashboard/PreviewType";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import AddCardWizardState from "@app/scene/dashboard/components/addEditCard/AddCardWizard/AddCardWizardState";
import Preview from "@app/scene/dashboard/components/addEditCard/components/Preview";
import ScopeTag from "@app/scene/dashboard/components/ScopeTag";
import { getOrganizationSettingValue, isMileageTrackingVisible } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import CardFeature from "../../enum/CardFeature";
import commonStyles from "../CommonStep.module.scss";

import CardFeatureFilter from "./components/CardFeatureFilter";
import CardList from "./components/CardList";
import styles from "./SelectCardTypeStep.module.scss";
import defaultCardConfiguration from "./utils/defaultCardConfiguration";

const messages = defineMessages({
  next: {
    id: "dashboard.addCardWizard.selectTypeStep.nextButton",
    defaultMessage: "Next Step",
    description: "Next button label in Add Card Wizard on the Dashboard page",
  },
  searchPlaceholder: {
    id: "dashboard.addCardWizard.selectTypeStep.search.placeholder",
    defaultMessage: "Search",
    description: "Search field placeholder in Add Card Wizard on the Dashboard page",
  },
  title: {
    id: "dashboard.addCardWizard.selectTypeStep.title",
    defaultMessage: "Add Chart: Select Type {scope}",
    description: "Select Type step title in Add Card Wizard on the Dashboard page",
  },
});

interface Props extends IWizardStep<AddCardWizardState> {
  activeCheckInsEnabled: boolean;
  isMileageTrackingEnabled: boolean;
}

const SelectCardTypeStep: React.FC<Props> = ({
  activeCheckInsEnabled,
  isMileageTrackingEnabled,
  onSetState,
  state,
  ...props
}) => {
  const intl = useIntl();

  const analytics = useAnalytics();
  useEffect(() => {
    analytics.using(["Select Card step"]);
  }, [analytics]);

  const handleSetCardType = useCallback(
    (cardType: CardType) => {
      analytics.clicked(["Chart type pill"]);
      onSetState((state) => ({
        ...state,
        cardType,
        configuration:
          !state.configuration || state.cardType !== cardType
            ? defaultCardConfiguration[cardType]
            : state.configuration,
      }));
    },
    [onSetState, analytics]
  );

  const [query, setQuery] = useState<string>("");
  const [featureFilter, setFeatureFilter] = useState<CardFeature | undefined>();

  useChangeTracking(() => {
    analytics.using(["search"]);
  }, [query, analytics]);

  return (
    <StandardStep
      backButtonHidden
      className={cn(commonStyles.container, styles.container)}
      nextButtonDisabled={
        !state.cardType ||
        !state.configuration ||
        (!activeCheckInsEnabled && state.cardType === CardType.CUSTOMER_FACE_TIME) ||
        (!isMileageTrackingEnabled && state.cardType === CardType.MILEAGE_TRACKING)
      }
      nextButtonText={intl.formatMessage(messages.next)}
      title={intl.formatMessage(messages.title, { scope: <ScopeTag scope={state.scope} /> })}
      {...props}
    >
      <div className={styles.preview}>
        <Preview
          configuration={state.configuration}
          previewType={PreviewType.SAMPLE_DATA}
          scope={state.scope}
          type={state.cardType}
        />
      </div>
      <div className={styles.types}>
        <SearchField
          allowClear
          onChange={setQuery}
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          size="large"
          value={query}
        />
        <CardFeatureFilter onChange={setFeatureFilter} value={featureFilter} />
        <CardList
          className={styles.list}
          featureFilter={featureFilter}
          isMileageTrackingEnabled={isMileageTrackingEnabled}
          onClick={handleSetCardType}
          query={query}
          selectedCard={state.cardType}
        />
      </div>
    </StandardStep>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeCheckInsEnabled: getOrganizationSettingValue(state)(OrganizationSetting.ACTIVE_CHECK_IN),
  isMileageTrackingEnabled: isMileageTrackingVisible(state),
});

export default connect(mapStateToProps)(SelectCardTypeStep);
